import { isRecord, metres, type Doc } from '@day';
import { googlePlace, lookup, LookupError, newSession, stubbed, type GooglePlace } from './maps';

/**
 * Finding a written address as the phone's OrderPlacer does (lib/services/orders/order_placer.dart): Geocoding first
 * (0.5¢), and the place search (autocomplete, then the top suggestion's details: 1.7¢) only when Geocoding's answer
 * isn't usable: nothing, only an area, another street, or a namesake far away. Every lookup goes through Routr's server
 * (maps.ts). On the emulators Geocoding by address isn't answered, so the search goes straight to the stub's places.
 */

export interface Found {
  /** Null when it wasn't found. */
  place: (GooglePlace & { area: string }) | null;
  /** Found only roughly: no house on the map, a plus code, or another street than the one written. */
  rough: boolean;
  via: 'geocode' | 'places' | null;
  /** Said when the lookup couldn't be made (the day's lookups used up, no connection). */
  error?: string;
  /** Why it couldn't be made: `limit` when the account's lookups for the day are used up (maps.ts LIMITS). */
  why?: LookupError['why'];
}

/** Farther than this from the shop, a match is a namesake in another city, not the place (`OrderPlacer.farKm`). */
const FAR_M = 100_000;

const LANDMARK = /^(off|opp\.?|opposite|beside|behind|near|by|close\s+to|after|before)\b/i;

/** The address without its "off …" / "opposite …" parts, or null when it has none (`withoutLandmarks`). */
export function withoutLandmarks(address: string): string | null {
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  const kept = parts.filter((p) => !LANDMARK.test(p));
  return kept.length === parts.length || !kept.length ? null : kept.join(', ');
}

/** Without a trailing postcode ("…, Abuja, 900103"): the search does worse with it (`withoutPostcode`). */
export const withoutPostcode = (address: string) => address.replace(/[,\s]+\d{5,6}\s*$/, '');

/** What to search for: the street the house is on, without its "off …" part or postcode (`searchQuery`). */
export const searchQuery = (address: string) => withoutPostcode(withoutLandmarks(address) ?? address);

const STREET = /\b(street|str|st|road|rd|avenue|ave|way|close|cl|crescent|cres|lane|ln|drive|dr|boulevard|blvd|court|ct|highway|hwy|expressway|expy|estate|layout|link)\b\.?/gi;

/** The words that name the street an address is on, or null when no part names a street (`streetWords`). */
export function streetWords(address: string): string[] | null {
  for (const part of address.split(',')) {
    const p = part.trim();
    STREET.lastIndex = 0;
    if (!p || LANDMARK.test(p) || !STREET.test(p)) continue;
    const words = p
      .replace(/^(plot|no\.?|house|flat|block)?\s*\d+[a-z]?\b/i, '')
      .replace(STREET, ' ')
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((w) => w.length > 1);
    if (words.length) return words;
  }
  return null;
}

/** Whether the map's address is on the street [written] names; true when it names none (`sameStreet`). */
export function sameStreet(written: string, found: string): boolean {
  const words = streetWords(written);
  if (!words) return true;
  const hay = found.toLowerCase();
  return words.every((w) => hay.includes(w));
}

const tooFar = (p: { lat: number; lng: number }, near?: { lat: number; lng: number } | null) => !!near && metres(near, p) > FAR_M;
const areaOf = (address: string) => address.split(',')[1]?.trim() ?? '';

/** Whether Geocoding's answer will do: more than an area, not a namesake far away, on the street written. */
export const usableGeocode = (p: GooglePlace, written: string, near?: { lat: number; lng: number } | null) => !p.areaOnly && !tooFar(p, near) && sameStreet(written, p.address);

function countryParams(country: string | null | undefined, withRegion: boolean): Doc {
  const c = country?.toLowerCase();
  return c ? { components: `country:${c}`, ...(withRegion ? { region: c } : {}) } : {};
}

/** The top suggestion's details, in a search session of its own. */
async function topPlace(query: string, country: string | null | undefined): Promise<GooglePlace | null> {
  const session = newSession();
  const a = await lookup('autocomplete', { input: query.slice(0, 200), sessiontoken: session, ...countryParams(country, false), language: 'en' });
  const top = (Array.isArray(a.predictions) ? a.predictions[0] : null) as Doc | null;
  if (!top || typeof top.place_id !== 'string') return null;
  const d = await lookup('details', { place_id: top.place_id, sessiontoken: session });
  return isRecord(d.result) ? googlePlace(d.result, false) : null;
}

/** Finds [address]: Geocoding first, the place search only when that isn't usable. [near]: the shop, to rule out namesakes. */
export async function findAddress(address: string, o: { country?: string | null; near?: { lat: number; lng: number } | null } = {}): Promise<Found> {
  const written = address.trim();
  try {
    if (!stubbed) {
      const g = await lookup('geocode', { address: searchQuery(written).slice(0, 300), ...countryParams(o.country, true), language: 'en' });
      const r = Array.isArray(g.results) ? (g.results[0] as Doc | undefined) : undefined;
      const gp = r && isRecord(r) ? googlePlace(r, true) : null;
      if (gp && usableGeocode(gp, written, o.near)) {
        return { place: { ...gp, area: areaOf(gp.address) }, rough: gp.precision === 'low' || r!.partial_match === true, via: 'geocode' };
      }
    }
    // The street the house is on first, without the "off …" part that pulls the search to the better-known street.
    const main = withoutLandmarks(written);
    if (main) {
      const p = await topPlace(searchQuery(main), o.country);
      if (p && !tooFar(p, o.near) && sameStreet(written, p.address)) return { place: { ...p, area: areaOf(p.address) }, rough: p.precision === 'low', via: 'places' };
    }
    const p = await topPlace(withoutPostcode(written), o.country);
    if (!p || tooFar(p, o.near)) return { place: null, rough: false, via: 'places' };
    const other = !sameStreet(written, p.address);
    return { place: { ...p, area: areaOf(p.address) }, rough: other || p.precision === 'low', via: 'places' };
  } catch (e) {
    return { place: null, rough: false, via: null, error: e instanceof LookupError ? e.message : 'Routr’s server didn’t answer. Check the connection and try again.', why: e instanceof LookupError ? e.why : 'unavailable' };
  }
}

/** A place Google knows by its ID, placed again (Geocoding by place ID, 0.5¢): a waiting stop going onto a route. */
export async function findPlaceId(placeId: string): Promise<GooglePlace | null> {
  const g = await lookup('geocode', { place_id: placeId, language: 'en' });
  const r = Array.isArray(g.results) ? (g.results[0] as Doc | undefined) : undefined;
  return r && isRecord(r) ? googlePlace(r, false) : null;
}

/**
 * Runs [work] over [items] [atOnce] at a time, in order, saying how many are done (`OrderPlacer.atOnce` is 8). Once
 * [signal] is aborted (the page left), nothing more starts: what's left stays undefined.
 */
export async function inTurn<T, R>(items: T[], work: (x: T, i: number) => Promise<R>, done?: (n: number) => void, atOnce = 8, signal?: AbortSignal): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  let n = 0;
  const worker = async () => {
    while (next < items.length && !signal?.aborted) {
      const i = next++;
      out[i] = await work(items[i], i);
      done?.(++n);
    }
  };
  await Promise.all(Array.from({ length: Math.min(atOnce, items.length) }, worker));
  return out;
}
